import { Stack } from '@mui/material';
import { FIELD_STATUS_VALUES } from 'business/constants';
import type { LifeCycleStatus } from 'business/types';
import GeneralModal from 'components/SharedComponents/Modals/GeneralModal';
import { flattenAndSortSoftwareByFunction } from 'components/SharedComponents/MultiSoftwareSelector/helpers';
import MultiSoftwareSelector from 'components/SharedComponents/MultiSoftwareSelector/MultiSoftwareSelector';
import { useAPIGetAllSoftwares } from 'modules/backoffice/api/integrations/useAPIGetAllSoftwares';
import { useAPIPutDataCatalogField } from 'modules/datacatalog/api/useAPIPutDataCatalogFields';
import type { FieldStatusData } from 'modules/datacatalog/types';
import type { Dispatch, SetStateAction } from 'react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { handleStatusEffectOnDate } from '../helpers/statusModalHelpers';
import FieldStatusDescriptionEditor from './FieldStatusDescriptionEditor';
import FieldStatusExpirationPicker from './FieldStatusExpirationPicker';
import FieldStatusSelector from './FieldStatusSelector';

type Props = {
    fieldName: string;
    setOpenFieldStatusModal: Dispatch<SetStateAction<boolean>>;
    isTable: boolean;
    fieldStatusData: FieldStatusData;
};

const EditFieldStatusModal = ({
    fieldName,
    setOpenFieldStatusModal,
    isTable,
    fieldStatusData,
}: Props) => {
    const { t } = useTranslation();

    const { data: softwareByFunction } = useAPIGetAllSoftwares();
    const allSoftware = useMemo(
        () => flattenAndSortSoftwareByFunction(softwareByFunction),
        [softwareByFunction],
    );

    const initialEndDate = fieldStatusData.statusEndDate;
    const initialStatus = fieldStatusData.lifeCycleStatus;

    const [status, setStatus] = useState(initialStatus);
    const [software, setSoftware] = useState(
        fieldStatusData.statusAffectedSoftware,
    );
    const [description, setDescription] = useState(
        fieldStatusData.statusDescription,
    );
    const [endDate, setEndDate] = useState<string | undefined>(initialEndDate);

    const isDefaultStatus = status === FIELD_STATUS_VALUES.DEFAULT;
    const isNew = status === FIELD_STATUS_VALUES.NEW;

    const [validationError, setValidationError] = useState(false);

    const isInvalidEndDate = (!endDate && isNew) || validationError;

    const { mutate } = useAPIPutDataCatalogField();

    const closeStatusModal = (): void => {
        setOpenFieldStatusModal(false);
    };

    const handleSaveStatusClick = (): void => {
        mutate({
            fieldName,
            lifeCycleStatus: status,
            statusAffectedSoftware: software?.length ? software : undefined,
            statusDescription: description,
            statusEndDate: initialEndDate || isNew ? endDate : undefined,
        });
        closeStatusModal();
    };

    const handleStatusChange = (newStatus: LifeCycleStatus): void => {
        setStatus(newStatus);

        handleStatusEffectOnDate(
            setEndDate,
            newStatus,
            initialStatus,
            endDate,
            initialEndDate,
        );
    };

    const cancelButton = {
        text: t('common.cancel'),
        onClick: closeStatusModal,
    };
    const submitButton = {
        text: t('common.confirm'),
        onClick: handleSaveStatusClick,
        disabled: isInvalidEndDate,
    };

    const title = isTable
        ? t('dataCatalog.fieldStatusManagement.tableModalTitle')
        : t('dataCatalog.fieldStatusManagement.fieldModalTitle');

    return (
        <GeneralModal
            title={title}
            isOpen={true}
            onClose={closeStatusModal}
            containedButton={submitButton}
            textButton={cancelButton}
            shouldCloseOnEscape
            shouldCloseOnBackdropClick
        >
            <Stack spacing={3}>
                <FieldStatusSelector
                    currentStatus={status}
                    onChange={handleStatusChange}
                />

                <MultiSoftwareSelector
                    softwares={allSoftware}
                    value={software ?? []}
                    onChange={setSoftware}
                    label={t(
                        'dataCatalog.fieldStatusManagement.affectedSoftware',
                    )}
                    placeholder={t(
                        'dataCatalog.fieldStatusManagement.softwareSelectorPlaceholder',
                    )}
                    disabled={isDefaultStatus}
                />

                <FieldStatusDescriptionEditor
                    description={description}
                    setDescription={setDescription}
                    disabled={isDefaultStatus}
                />

                <FieldStatusExpirationPicker
                    endDate={endDate}
                    setEndDate={setEndDate}
                    validationError={validationError}
                    setValidationError={setValidationError}
                    disabled={!isNew}
                />
            </Stack>
        </GeneralModal>
    );
};
export default EditFieldStatusModal;
